import {
  registerComponent,
  getComponentConfig,
  debounce,
  asyncRequest,
  replaceDOM,
  loading,
} from "helpers";

function variantOptionRow(variant, n) {
  return Array.from({ length: n }, (_, i) => {
    const raw = variant.options?.[i];
    if (raw != null && raw !== "") {
      return typeof raw === "string" ? raw : String(raw?.value ?? "");
    }
    const v = variant[`option${i + 1}`];
    return v != null && v !== "" ? String(v) : "";
  });
}

function selectionMatchesSomeVariant(variants, selectedNames) {
  const n = selectedNames.length;
  return variants.some((v) => {
    const row = variantOptionRow(v, n);
    return selectedNames.every((s, i) => s == null || row[i] === s);
  });
}

registerComponent(
  "product-options",
  class ProductOptions extends HTMLElement {
    // "*" will be replaced with product id
    ELEMENTS_TO_REPLACE = [
      '[data-product-media="*"]',
      '[data-product-prices="*"]',
      '[data-add-to-cart="*"]',
      '[data-inventory-messaging="*"]',
      '[data-sku-display="*"]',
      '[data-product-options="*"]',
      '[data-product-availability="*"]',
      '[data-additional-info="*"]',
      '[data-why-strolleria="*"]',
    ];

    constructor() {
      super();

      this.config = getComponentConfig(this);
      this.options = this.querySelectorAll("[data-product-option]");
      this.selectedOptions = this.getSelectedOptions();
      [this.productPath, this.productParams] = this.config.url.split("?");
      this.isPDP = window.location.pathname === this.productPath;

      const debouncedHandler = debounce((event) => {
        this.handleOptionChange(event);
      }, 300);

      this.options.forEach((option) => {
        option.addEventListener("change", debouncedHandler.bind(this));
      });
    }

    handleOptionChange(event) {
      // Stop all videos when variant changes
      setTimeout(() => {
        this.stopAllVideos();
      }, 100);
      
      const productUrl = event.target.dataset.productUrl;
      loading.add(this);

      if (productUrl) {
        this.updateProductDOM(productUrl);
      } else {
        this.ensureValidVariantSelection();
        this.selectedOptions = this.getSelectedOptions();
        this.updateProductDOM();
      }
    }

    // Method to stop all video players when variant changes
    stopAllVideos() {
      console.log('Stopping videos due to variant change...');
      
      // Use the global stopAllVideos function
      if (typeof window.stopAllVideos === 'function') {
        window.stopAllVideos();
      } else {
        // Fallback: Use the global allVideoPlayers array from video-player.js
        if (typeof allVideoPlayers !== 'undefined') {
          console.log('Found video players in global array:', allVideoPlayers.length);
          
          allVideoPlayers.forEach(player => {
            if (player.iframe) {
              console.log('Stopping video player:', player);
              player.stopVideo();
            }
          });
        }
        
        // Fallback: Find all video player instances and stop them
        const videoPlayers = document.querySelectorAll('video-player');
        console.log('Found video players via querySelector:', videoPlayers.length);
        
        videoPlayers.forEach(player => {
          if (player.iframe) {
            console.log('Stopping video player:', player);
            player.stopVideo();
          }
        });
        
        // Also try to stop videos by removing iframes directly
        const iframes = document.querySelectorAll('video-player iframe');
        iframes.forEach(iframe => {
          console.log('Removing iframe directly:', iframe);
          iframe.remove();
        });
        
        // Remove video-player--playing class from all video players
        const playingVideos = document.querySelectorAll('video-player.video-player--playing');
        playingVideos.forEach(video => {
          console.log('Removing playing class from:', video);
          video.classList.remove('video-player--playing');
        });
      }
    }

    getSelectedOptions() {
      const checkedInputs = Array.from(
        this.querySelectorAll("[data-product-option]:checked")
      );

      return checkedInputs.map((input) => input.dataset.productOption);
    }

    updateOptionLabelSpans() {
      this.querySelectorAll(".product-options__option-group").forEach((fieldset) => {
        const checked = fieldset.querySelector("input[data-product-option]:checked");
        const span = fieldset.querySelector(".product-options__option-name-value");
        if (checked && span) span.textContent = checked.value;
      });
    }

    ensureValidVariantSelection() {
      const product = this.config.product;
      const names = product.options || [];
      const n = names.length;
      if (n < 2) return;

      const variants = product.variants || [];
      if (!variants.length) return;

      const selected = names.map(
        (optionName) =>
          this.querySelector(`input[name="${CSS.escape(optionName)}"]:checked`)
            ?.value ?? null
      );

      if (selectionMatchesSomeVariant(variants, selected)) {
        this.updateOptionLabelSpans();
        return;
      }

      const firstSel = selected[0];
      const pick =
        (firstSel != null &&
          variants.find((v) => variantOptionRow(v, n)[0] === firstSel)) ||
        variants[0];

      const target = variantOptionRow(pick, n);
      names.forEach((optionName, i) => {
        const want = target[i];
        for (const input of this.querySelectorAll(
          `input[name="${CSS.escape(optionName)}"]`
        )) {
          if (input.value === want) {
            input.checked = true;
            break;
          }
        }
      });

      this.updateOptionLabelSpans();
    }

    async updateProductDOM(productUrl) {
      const url = productUrl ? productUrl : this.getURL();
      const response = await asyncRequest(url);

      if (response) {
        this.handleSuccessfulUpdate(response, productUrl);
      } else {
        this.handleUnsuccessfulUpdate(productUrl);
      }

      loading.remove(this);
    }

    handleSuccessfulUpdate(html, productUrl) {
      const url = productUrl ? productUrl : this.getURL();

      if (this.isPDP) {
        window.scrollTo({
          top: 0,
        });
      }

      if (productUrl) {
        document.documentElement.innerHTML = html;
      } else {
        this.ELEMENTS_TO_REPLACE.forEach((selector) => {
          const DOMspecificPlaceholder = selector.replace(
            "*",
            this.config.product.id
          );

          replaceDOM(html, DOMspecificPlaceholder);
        });
      }

      if (this.isPDP) {
        window.history.replaceState(null, "", url);
      }
    }

    handleUnsuccessfulUpdate(productUrl) {
      if (this.isPDP) {
        if (productUrl) {
          window.location.href = productUrl;
        } else {
          window.location.href = this.getURL();
        }
      }
    }

    getURL() {
      const params = window.location.search;
      const search = new URLSearchParams(params);

      search.set("option_values", this.selectedOptions.join(","));

      return `${window.location.origin}${this.config.url}?${search.toString()}`;
    }
  }
);
